import cn from "classnames";
import { Device, DeviceErrorSortKey, Errors, ErrorSummary, ErrorIssueStatus, ErrorIssueUpdateRequest, getErrorIssueStatusDisplayName, useAuth } from "@app/shared";
import { useDialog } from "../../../hooks";
import { IconLink, Icons, Link, PageContainer, SearchInput, SortableTable, SortableTableHeaderType, ServerSideOptions } from "../../shared";
import { DeviceErrorTrendCard } from "./DeviceErrorTrendCard";
import { DeviceErrorFirmwareScatterCard } from "./DeviceErrorFirmwareScatterCard";
import { HoverTooltip } from "../../shared/HoverTooltip";
import { FilterOption } from "../../shared/Filter";
import { formatInTimeZone } from "date-fns-tz";
import copy from "copy-to-clipboard";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Pages } from "..";
import { DeviceTab } from "../facility/deviceTabs";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { toastWarning, toastSuccess, debounce } from "../../../helpers";

type MappedErrorSummary = ErrorSummary & { id: string };

type DeviceErrorsQuery = {
    page: number;
    pageSize: number;
    sortKey?: DeviceErrorSortKey;
    sortDirection?: "asc" | "desc";
    filters?: FilterOption<MappedErrorSummary>[];
};

const STATUS_FILTER_OPTIONS = [
    { value: String(ErrorIssueStatus.Unresolved), label: "Unresolved" },
    { value: String(ErrorIssueStatus.Regressed), label: "Regressed" },
    { value: String(ErrorIssueStatus.Resolved), label: "Resolved" },
    { value: String(ErrorIssueStatus.Ignored), label: "Ignored" },
];

const statusBadgeClasses = (status: ErrorIssueStatus): string => {
    switch (status) {
        case ErrorIssueStatus.Resolved:
            return "bg-green-100 text-green-800";
        case ErrorIssueStatus.Regressed:
            return "bg-red-100 text-red-800";
        case ErrorIssueStatus.Ignored:
            return "bg-gray-100 text-gray-600";
        default:
            return "bg-amber-100 text-amber-800";
    }
};

const ACTION_TOOLTIPS: { [key in ErrorIssueStatus]?: string } = {
    [ErrorIssueStatus.Resolved]: "Mark this issue fixed. If it occurs again it will be flagged as Regressed.",
    [ErrorIssueStatus.Ignored]: "Acknowledge this issue as known and not being actioned. Requires a reason.",
    [ErrorIssueStatus.Unresolved]: "Reopen this issue and move it back to the actionable view.",
};

const isNewIssue = (firstOccurrence?: Date): boolean => {
    if (!firstOccurrence) return false;
    return Date.now() - new Date(firstOccurrence).getTime() < 24 * 60 * 60 * 1000;
};

type ErrorsPageSnapshot = {
    data: ErrorSummary[];
    totalCount: number;
    totalOccurrences: number;
    totalDevices: number;
    latestFirmware?: string;
    at: number;
};

// Module-level stale-while-revalidate cache: navigating to a device and back remounts
// this component, and repainting the last response instantly (while a fresh fetch runs
// in the background) makes the round trip feel immediate. Cleared on any issue-status
// update so a resolve/ignore can never flash pre-update rows.
const errorsPageCache = new Map<string, ErrorsPageSnapshot>();
const ERRORS_PAGE_CACHE_TTL_MS = 5 * 60_000;
const ERRORS_PAGE_CACHE_MAX_ENTRIES = 50;

const sortKeyFromDataKey = (sortKey?: string | null): DeviceErrorSortKey =>
    sortKey === "lastOccurrence" ? DeviceErrorSortKey.LastOccurrence
    : sortKey === "firstOccurrence" ? DeviceErrorSortKey.FirstOccurrence
    : sortKey === "hashId" ? DeviceErrorSortKey.HashId
    : sortKey === "latestFirmware" ? DeviceErrorSortKey.LatestFirmware
    : sortKey === "count" ? DeviceErrorSortKey.Count
    : sortKey === "count24h" ? DeviceErrorSortKey.Count24h
    : sortKey === "count7d" ? DeviceErrorSortKey.Count7d
    : sortKey === "status" ? DeviceErrorSortKey.Status
    : DeviceErrorSortKey.Count24h;

export const DeviceErrorsTable: React.FC<{ device?: Device }> = (props) => {
    const [deviceErrors, setDeviceErrors] = useState<ErrorSummary[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [totalOccurrences, setTotalOccurrences] = useState(0);
    const [totalDevices, setTotalDevices] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    // In the standalone (fleet-wide) view the table state lives in the URL, so navigating
    // to a device and pressing Back lands on the same page/sort. The device-scoped tab
    // shares its URL with the rest of DeviceDetails, so it keeps plain in-memory state.
    const urlState = props.device ? null : searchParams;
    const [query, setQuery] = useState<DeviceErrorsQuery>(() => ({
        page: Math.max(1, parseInt(urlState?.get("errPage") ?? "", 10) || 1),
        pageSize: parseInt(urlState?.get("errSize") ?? "", 10) || 15,
        sortKey: sortKeyFromDataKey(urlState?.get("errSort")),
        sortDirection: urlState?.get("errDir") === "asc" ? "asc" : "desc",
    }));

    const { numberFormatter } = useAuth();
    const confirm = useDialog<{ message: string; stack: string }>();
    const resolveDialog = useDialog<string>();
    const reasonDialog = useDialog<string>();
    const ignoreDialog = useDialog<string>();
    const resolveFwRef = useRef("");
    const [resolveFw, setResolveFw] = useState("");
    const ignoreReasonRef = useRef("");
    const [ignoreReason, setIgnoreReason] = useState("");
    const [hideIgnored, setHideIgnored] = useState(() => urlState?.get("errHideIgnored") !== "0");
    const [onlyLatestFirmware, setOnlyLatestFirmware] = useState(() => urlState?.get("errLatestFw") === "1");
    // Free-text search across the issue hash/message/stack trace and the reporting device's
    // name/key/description/serial — replaces the old "Key" column filter (a bare hash
    // substring match nobody used since nobody has hashes memorized).
    const [search, setSearch] = useState(() => urlState?.get("errSearch") ?? "");
    // The newest firmware version in scope, reported back by the API so the toggle can name it.
    const [latestFirmware, setLatestFirmware] = useState<string | undefined>();

    const convertFiltersToParams = useCallback(
        (filters?: FilterOption<MappedErrorSummary>[]) => {
            const params: {
                deviceId?: string;
                dateFrom?: Date;
                dateTo?: Date;
                status?: ErrorIssueStatus;
            } = {};

            if (props.device) {
                params.deviceId = props.device.id;
            }

            if (!filters) return params;

            filters.forEach((filter) => {
                if (filter.value !== undefined && filter.value !== "") {
                    switch (filter.id) {
                        case "devices":
                            if (!props.device) {
                                // Only allow device filter if not already filtered by device prop
                                params.deviceId = filter.value;
                            }
                            break;
                        case "lastOccurrence":
                            if (filter.value && typeof filter.value === "object") {
                                if (filter.value.from) params.dateFrom = new Date(filter.value.from);
                                if (filter.value.to) params.dateTo = new Date(filter.value.to);
                            }
                            break;
                        case "status":
                            params.status = Number(filter.value) as ErrorIssueStatus;
                            break;
                    }
                }
            });

            return params;
        },
        [props.device]
    );

    const fetchErrors = useCallback(async () => {
        const filterParams = convertFiltersToParams(query.filters);
        const cacheKey = JSON.stringify({
            device: props.device?.id ?? null,
            page: query.page,
            pageSize: query.pageSize,
            sortKey: query.sortKey,
            sortDirection: query.sortDirection,
            filterParams,
            hideIgnored,
            onlyLatestFirmware,
            search,
        });

        // Stale-while-revalidate: paint the cached page immediately (no spinner), then
        // let the fetch below replace it with fresh data.
        const applySnapshot = (s: ErrorsPageSnapshot) => {
            setDeviceErrors(s.data);
            setTotalCount(s.totalCount);
            setTotalOccurrences(s.totalOccurrences);
            setTotalDevices(s.totalDevices);
            setLatestFirmware(s.latestFirmware);
        };
        const cached = errorsPageCache.get(cacheKey);
        const hasFreshCache = !!cached && Date.now() - cached.at < ERRORS_PAGE_CACHE_TTL_MS;
        if (hasFreshCache) {
            applySnapshot(cached);
        }
        setIsLoading(!hasFreshCache);

        try {
            const trimmedSearch = search.trim() || undefined;
            const response = props.device
                ? await Errors.getDeviceErrorsPaginated(props.device.id, query.page, query.pageSize, query.sortKey, query.sortDirection, undefined, trimmedSearch, filterParams.dateFrom, filterParams.dateTo, filterParams.status, hideIgnored, onlyLatestFirmware)
                : await Errors.getAllDeviceErrorsPaginated(query.page, query.pageSize, query.sortKey, query.sortDirection, undefined, trimmedSearch, filterParams.deviceId, filterParams.dateFrom, filterParams.dateTo, filterParams.status, hideIgnored, onlyLatestFirmware);

            const snapshot: ErrorsPageSnapshot = {
                data: response?.data || [],
                totalCount: response?.totalCount || 0,
                totalOccurrences: response?.totalOccurrences || 0,
                totalDevices: response?.totalDevices || 0,
                latestFirmware: response?.latestFirmware ?? undefined,
                at: Date.now(),
            };
            if (errorsPageCache.size >= ERRORS_PAGE_CACHE_MAX_ENTRIES) {
                errorsPageCache.clear();
            }
            errorsPageCache.set(cacheKey, snapshot);
            applySnapshot(snapshot);
        } catch (error) {
            console.error("Failed to fetch device errors:", error);
            toastWarning("Failed to load device errors. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    }, [props.device, query, convertFiltersToParams, hideIgnored, onlyLatestFirmware, search]);

    const debouncedFetchErrors = useMemo(() => debounce(fetchErrors, 300), [fetchErrors]);

    // Fetch immediately on mount — the debounce only exists to coalesce rapid
    // query changes (typing in filters), and delaying the initial load 300ms
    // just makes the page feel slower.
    const isFirstFetch = useRef(true);
    useEffect(() => {
        if (isFirstFetch.current) {
            isFirstFetch.current = false;
            fetchErrors();
            return;
        }
        debouncedFetchErrors();
    }, [fetchErrors, debouncedFetchErrors]);

    const handleSort = (sortKey: string, direction: "asc" | "desc") => {

        const sortKeyEnum =
            sortKey === "lastOccurrence" ? DeviceErrorSortKey.LastOccurrence
            : sortKey === "firstOccurrence" ? DeviceErrorSortKey.FirstOccurrence
            : sortKey === "hashId" ? DeviceErrorSortKey.HashId
            : sortKey === "latestFirmware" ? DeviceErrorSortKey.LatestFirmware
            : sortKey === "count" ? DeviceErrorSortKey.Count
            : sortKey === "count24h" ? DeviceErrorSortKey.Count24h
            : sortKey === "count7d" ? DeviceErrorSortKey.Count7d
            : sortKey === "status" ? DeviceErrorSortKey.Status
            : DeviceErrorSortKey.Count24h;

        setQuery((prev) => ({
            ...prev,
            sortKey: sortKeyEnum,
            sortDirection: direction,
            page: 1,
        }));
    };

    const handleFilter = (filters: FilterOption<MappedErrorSummary>[]) => {
        setQuery((prev) => ({
            ...prev,
            filters,
            page: 1,
        }));
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setQuery((prev) => ({
            ...prev,
            page,
            pageSize,
        }));
    };

    const getSortKeyDataKey = (sortKey?: DeviceErrorSortKey): string | undefined => {
        switch (sortKey) {
            case DeviceErrorSortKey.LastOccurrence:
                return "lastOccurrence";
            case DeviceErrorSortKey.FirstOccurrence:
                return "firstOccurrence";
            case DeviceErrorSortKey.HashId:
                return "hashId";
            case DeviceErrorSortKey.LatestFirmware:
                return "latestFirmware";
            case DeviceErrorSortKey.Count:
                return "count";
            case DeviceErrorSortKey.Count24h:
                return "count24h";
            case DeviceErrorSortKey.Count7d:
                return "count7d";
            case DeviceErrorSortKey.Status:
                return "status";
            default:
                return undefined;
        }
    };

    // Mirror table state into the URL (replace, not push, so it doesn't pollute history).
    // Defaults are omitted to keep the URL clean; `tab` and other params are preserved.
    useEffect(() => {
        if (props.device) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            const setOrDelete = (key: string, value: string, defaultValue: string) =>
                value === defaultValue ? next.delete(key) : next.set(key, value);
            setOrDelete("errPage", String(query.page), "1");
            setOrDelete("errSize", String(query.pageSize), "15");
            setOrDelete("errSort", getSortKeyDataKey(query.sortKey) ?? "count24h", "count24h");
            setOrDelete("errDir", query.sortDirection ?? "desc", "desc");
            setOrDelete("errHideIgnored", hideIgnored ? "1" : "0", "1");
            setOrDelete("errLatestFw", onlyLatestFirmware ? "1" : "0", "0");
            setOrDelete("errSearch", search, "");
            return next;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.device, query.page, query.pageSize, query.sortKey, query.sortDirection, hideIgnored, onlyLatestFirmware, search, setSearchParams]);

    const handleSearch = (newSearch: string) => {
        setSearch(newSearch);
        setQuery((prev) => ({ ...prev, page: 1 }));
    };

    const serverSideOptions: ServerSideOptions<MappedErrorSummary> = {
        enabled: true,
        onSort: handleSort,
        onFilter: handleFilter,
        onPageChange: handlePageChange,
        totalCount,
        currentPage: query.page,
        pageSize: query.pageSize,
        sortKey: getSortKeyDataKey(query.sortKey),
        sortDirection: query.sortDirection,
    };

    const handleDetails = (message: string, stack: string) => {
        confirm.show("Stack Trace", "OK", { message, stack }, (_) => { });
    };

    // Build a link to a device's errors tab. Rendered as a real anchor (react-router Link)
    // so it supports right-click → "open in new tab".
    const deviceErrorsUrl = (deviceId: string) => Pages.DeviceDetails.getUrlWithQuery({ id: deviceId }, { tab: DeviceTab.Errors });

    const updateStatus = async (hashId: string, status: ErrorIssueStatus, resolvedInFirmwareVersion?: string, notes?: string) => {
        try {
            await Errors.updateDeviceErrorIssue(
                hashId,
                new ErrorIssueUpdateRequest({ status, resolvedInFirmwareVersion, severity: undefined, notes })
            );
            toastSuccess(`Issue marked ${getErrorIssueStatusDisplayName(status)}`);
            // Drop all cached pages — the refetch below must not repaint pre-update rows.
            errorsPageCache.clear();
            fetchErrors();
        } catch (error) {
            console.error("Failed to update error issue:", error);
            toastWarning("Failed to update the issue. Please try again.");
        }
    };

    const promptResolve = (hashId: string) => {
        resolveFwRef.current = "";
        setResolveFw("");
        resolveDialog.show("Resolve Issue", "Resolve", hashId, async () => {
            const fw = resolveFwRef.current.trim();
            await updateStatus(hashId, ErrorIssueStatus.Resolved, fw && fw.toUpperCase() !== "NA" ? fw : undefined);
        });
    };

    const promptIgnore = (hashId: string) => {
        ignoreReasonRef.current = "";
        setIgnoreReason("");
        ignoreDialog.show("Ignore Issue", "Ignore", hashId, async () => {
            const reason = ignoreReasonRef.current.trim();
            if (!reason) {
                throw new Error("A reason is required to ignore an issue.");
            }
            await updateStatus(hashId, ErrorIssueStatus.Ignored, undefined, reason);
        });
    };

    return (
        <>
            {props.device && <div className="p-5 m-5"> </div>}
            {/* Below 600px (the table's card-view cutover) only the trend card is shown —
                the summary and scatter cards eat too much vertical space on a phone. The
                wrapper divs carry the responsive visibility. */}
            <div className="flex flex-wrap gap-4">
                {/* The three single-value counts collapsed into one compact leftmost card. */}
                <div className="hidden sm:flex flex-1 min-w-[13rem] border border-separators-page p-5 gap-3 shadow-sm">
                    <div className="text-center bg-background-page-highlight-gray rounded-full h-16 w-16 flex flex-col items-center shrink-0">
                        <Icons.Details className="h-10 w-10 mx-auto my-auto" />
                    </div>
                    <div className="flex flex-col flex-grow justify-center gap-1.5 tabular-nums">
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="uppercase text-xs text-gray-500">Entries</span>
                            <span className="text-2xl font-bold">{numberFormatter.format(totalCount)}</span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="uppercase text-xs text-gray-500">Occurrences</span>
                            <span className="text-2xl font-bold">{numberFormatter.format(totalOccurrences)}</span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="uppercase text-xs text-gray-500">Devices</span>
                            <span className="text-2xl font-bold">{numberFormatter.format(totalDevices)}</span>
                        </div>
                    </div>
                </div>
                {/* Fleet-wide only — a single device rarely spans enough builds to be useful. */}
                {!props.device && (
                    <div className="hidden sm:flex flex-[2] min-w-[24rem]">
                        <DeviceErrorFirmwareScatterCard />
                    </div>
                )}
                <DeviceErrorTrendCard device={props.device} />
            </div>

            <PageContainer title="Recent Errors">
                <div className="flex flex-wrap items-center justify-between gap-4 pb-2">
                    <SearchInput
                        onSearch={handleSearch}
                        initialSearch={search}
                        placeholder="Search errors, messages, devices…"
                    />
                    <div className="flex flex-wrap items-center gap-4">
                    <HoverTooltip
                        hoverable={
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={onlyLatestFirmware}
                                    onChange={(e) => {
                                        setOnlyLatestFirmware(e.target.checked);
                                        setQuery((prev) => ({ ...prev, page: 1 }));
                                    }}
                                />
                                New in {latestFirmware ? `v${latestFirmware}` : "latest firmware"}
                            </label>
                        }
                    >
                        <div className="max-w-xs text-xs text-gray-600">
                            Show only issues whose first occurrence was on the newest reported firmware build
                            {latestFirmware ? ` (v${latestFirmware})` : ""} — i.e. regressions introduced by the latest release.
                        </div>
                    </HoverTooltip>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={hideIgnored}
                            onChange={(e) => setHideIgnored(e.target.checked)}
                        />
                        Hide ignored issues
                    </label>
                    </div>
                </div>
                <SortableTable
                    isLoading={isLoading}
                    columns={[
                        {
                            header: "Key",
                            dataKey: "hashId",
                            renderFunc: (data) =>
                                data?.hashId ? (
                                    <div className="flex items-center gap-2">
                                        <HoverTooltip hoverable={<span  title={data.hashId}>
                                            Key
                                        </span>}>{data.hashId}</HoverTooltip>
                                        <IconLink
                                            Icon={Icons.Clipboard}
                                            onClick={() => {
                                                copy(data.hashId!);
                                                toastSuccess("Copied", undefined, 1500);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    ""
                                ),
                            width: 50,
                            sortable: false,
                        },
                        {
                            header: "Status",
                            dataKey: "status",
                            renderFunc: (data) => {
                                const status = data?.status ?? ErrorIssueStatus.Unresolved;
                                const resolvedIn = data?.details?.resolvedInFirmwareVersion;
                                return (
                                    <div className="flex flex-col gap-1">
                                        <span className={cn("inline-block rounded px-2 py-0.5 text-xs font-semibold w-fit", statusBadgeClasses(status))}>
                                            {getErrorIssueStatusDisplayName(status)}
                                        </span>
                                        {status === ErrorIssueStatus.Resolved && <div className="text-xs text-gray-500">resolved in {resolvedIn || "N/A"}</div>}
                                        {status === ErrorIssueStatus.Ignored && data?.details?.notes && (
                                            <div
                                                className="text-xs text-gray-500 italic break-words line-clamp-2 cursor-pointer hover:text-brand-primary"
                                                title={data.details.notes}
                                                onClick={() => reasonDialog.show("Ignore Reason", "OK", data.details!.notes!, (_) => { })}
                                            >
                                                “{data.details.notes}”
                                            </div>
                                        )}
                                        {isNewIssue(data?.firstOccurrence) && <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold w-fit bg-blue-100 text-blue-800">New</span>}
                                    </div>
                                );
                            },
                            width: 90,
                            sortable: true,
                            filterable: true,
                            filterType: "select",
                            filterOptions: STATUS_FILTER_OPTIONS,
                        },
                        {
                            header: "Last Occurrence (UTC)",
                            dataKey: "lastOccurrence",
                            renderFunc: (data) => <div className="w-100 break-words">{data?.lastOccurrence ? formatInTimeZone(data.lastOccurrence, "UTC", "MMM d, yyyy HH:mm:ss") : ""}</div>,
                            width: 100,
                            sortable: true,
                            filterable: true,
                            filterType: "daterange",
                        },
                        {
                            header: "24h",
                            dataKey: "count24h",
                            renderFunc: (data) => <span>{numberFormatter.format(data?.count24h ?? 0)}</span>,
                            width: 50,
                            sortable: true,
                        },
                        {
                            header: "7d",
                            dataKey: "count7d",
                            renderFunc: (data) => <span>{numberFormatter.format(data?.count7d ?? 0)}</span>,
                            width: 50,
                            sortable: true,
                        },
                        {
                            header: "Total",
                            dataKey: "count",
                            renderFunc: (data) => <span>{numberFormatter.format(data?.count ?? 0)}</span>,
                            width: 60,
                            sortable: true,
                        },
                        {
                            header: "Message",
                            dataKey: "details",
                            renderFunc: (data) => <div className="w-100 break-words">{data?.details?.details}</div>,
                            width: 300,
                        },
                        {
                            // Buckets are scoped to the viewed device here, so latest/first firmware
                            // are the builds *this device* was on when it hit the error. We show the
                            // most recent, and (if it first appeared on an earlier build) where it started.
                            header: props.device ? "Firmware" : "Latest Firmware",
                            dataKey: "latestFirmware",
                            renderFunc: (data) => (
                                <div className="w-100 break-words">
                                    {data?.latestFirmware}
                                    {data?.firstFirmware && data.firstFirmware !== data.latestFirmware && (
                                        <div className="text-xs text-gray-500">first: {data.firstFirmware}</div>
                                    )}
                                </div>
                            ),
                            width: 80,
                        },
                        // On a single device's error tab the devices column is just that device
                        // repeated on every row, so omit it there. The list arrives from the
                        // server ordered by each device's most recent occurrence of the error.
                        ...(props.device ? [] : [{
                            header: "Devices",
                            dataKey: "devices",
                            renderFunc: (data) => (
                                // pr-4 gives the version tag breathing room from the next column
                                // (View Stack) — the grid's own cell padding alone reads as none
                                // on a wide monitor.
                                <div className="overflow-auto max-h-64 pr-4">
                                    <ul className="grid grid-cols-[1rem_1fr_auto] gap-x-1 items-center">
                                        {data?.devices?.map((d) => (
                                            // Grid (not an inline flex row) so all three fields land in real
                                            // columns: the icon cell is a fixed width whether or not this
                                            // particular device is cellular, the name starts at the same x on
                                            // every row, and the firmware version right-aligns in its own column
                                            // instead of drifting with name length.
                                            //
                                            // "group" on a `display: contents` element still works for
                                            // hover — contents only opts the li out of generating its own
                                            // box, it doesn't remove it from the hover/ancestor chain — so
                                            // hovering any cell below lights up all three as one row,
                                            // layered under the existing name-color hover rather than
                                            // replacing it.
                                            <li key={d.id} className="contents group">
                                                <span className="flex justify-center rounded group-hover:bg-background-page-highlight-gray">
                                                    {d.isCellular && (
                                                        <Icons.CellSignal className="text-gray-500" title="Cellular device" />
                                                    )}
                                                </span>
                                                <RouterLink
                                                    to={deviceErrorsUrl(d.id!)}
                                                    className="text-link-page hover:text-link-page-hover truncate rounded px-1 group-hover:bg-background-page-highlight-gray"
                                                    title={`Total hits: ${numberFormatter.format(d.count ?? 0)}${d.lastOccurrence ? ` — Last hit: ${formatInTimeZone(d.lastOccurrence, "UTC", "MMM d, yyyy HH:mm")} UTC` : ""}`}
                                                >
                                                    {d.name || d.id}
                                                </RouterLink>
                                                <span
                                                    className="text-xs text-gray-500 whitespace-nowrap justify-self-end rounded px-1 group-hover:bg-background-page-highlight-gray"
                                                    title={d.firmwareVersion ? `Current firmware: ${d.firmwareVersion}` : undefined}
                                                >
                                                    {d.firmwareVersion}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                            width: 104,
                            sortable: false,
                            filterable: true,
                            filterType: "text",
                        }] satisfies SortableTableHeaderType<MappedErrorSummary>[]),
                        {
                            header: "",
                            renderFunc: (fe) => (fe?.details?.stackTrace?.length ? <Link onClick={() => handleDetails(fe.details?.details ?? "", fe.details!.stackTrace!)}>View Stack</Link> : ""),
                            width: 50,
                        },
                        {
                            header: "Actions",
                            renderFunc: (fe) => {
                                if (!fe?.hashId) return "";
                                const status = fe.status ?? ErrorIssueStatus.Unresolved;
                                return (
                                    <div className="flex flex-col gap-1">
                                        {status !== ErrorIssueStatus.Resolved && status !== ErrorIssueStatus.Ignored && (
                                            <HoverTooltip hoverable={<Link onClick={() => promptResolve(fe.hashId!)}>Resolve</Link>}>
                                                {ACTION_TOOLTIPS[ErrorIssueStatus.Resolved]}
                                            </HoverTooltip>
                                        )}
                                        {status !== ErrorIssueStatus.Ignored && status !== ErrorIssueStatus.Resolved && (
                                            <HoverTooltip hoverable={<Link onClick={() => promptIgnore(fe.hashId!)}>Ignore</Link>}>
                                                {ACTION_TOOLTIPS[ErrorIssueStatus.Ignored]}
                                            </HoverTooltip>
                                        )}
                                        {status !== ErrorIssueStatus.Unresolved && (
                                            <HoverTooltip hoverable={<Link onClick={() => updateStatus(fe.hashId!, ErrorIssueStatus.Unresolved)}>Reopen</Link>}>
                                                {ACTION_TOOLTIPS[ErrorIssueStatus.Unresolved]}
                                            </HoverTooltip>
                                        )}
                                    </div>
                                );
                            },
                            width: 70,
                        },
                    ]}
                    data={deviceErrors?.map((de) => ({ ...de, id: de.hashId ?? "" }))}
                    pagination
                    defaultPageSize={15}
                    pageSizeOptions={[10, 15, 25, 50, 100]}
                    filtering
                    serverSide={serverSideOptions}
                />
            </PageContainer>
            {resolveDialog.renderDialog(
                (hashId) => (
                    <div className="flex flex-col gap-3">
                        <p className="text-sm">
                            Mark this issue resolved. Enter the firmware version it is fixed in, or <span className="font-semibold">NA</span> if not
                            applicable. If the issue recurs on a later firmware version it will be flagged as Regressed.
                        </p>
                        <input
                            autoFocus
                            className="border rounded px-3 py-2"
                            placeholder="e.g. 1.2.3.4 or NA"
                            defaultValue=""
                            value={resolveFw}
                            onChange={(e) => {
                                resolveFwRef.current = e.target.value;
                                setResolveFw(e.target.value);
                            }}
                        />
                        <p className="text-xs text-gray-500 break-all">{hashId}</p>
                    </div>
                ),
                { className: "w-[28rem]", closeOnOutsideClick: true }
            )}
            {ignoreDialog.renderDialog(
                (hashId) => (
                    <div className="flex flex-col gap-3">
                        <p className="text-sm">
                            Ignore this issue — acknowledge it as known and not being actioned. A reason is required.
                        </p>
                        <textarea
                            autoFocus
                            className="border rounded px-3 py-2"
                            rows={3}
                            placeholder="Why is this issue being ignored?"
                            value={ignoreReason}
                            onChange={(e) => {
                                ignoreReasonRef.current = e.target.value;
                                setIgnoreReason(e.target.value);
                            }}
                        />
                        <p className="text-xs text-gray-500 break-all">{hashId}</p>
                    </div>
                ),
                { className: "w-[28rem]", closeOnOutsideClick: true }
            )}
            {reasonDialog.renderDialog(
                (reason) => (
                    <div className="flex-1 overflow-y-auto max-h-[60vh] whitespace-pre-wrap break-words text-sm">{reason}</div>
                ),
                { className: "w-[28rem]", closeOnOutsideClick: true }
            )}
            {confirm.renderDialog(
                (model) => {
                    if (!model) {
                        return <></>;
                    }

                    const { message, stack } = model;
                    var lines = stack.split(/(?:\s+at\s+| (?=\n))/); // splits on ' at ' and ' \n'

                    return (
                        <div className="flex-1 flex flex-col min-h-0">
                            {/* The message is the "which error am I looking at" header — without it,
                                a stack trace opened a minute ago is indistinguishable from any other. */}
                            {message && (
                                <div className="font-semibold break-words pb-3 mb-3 border-b border-separators-page shrink-0">
                                    {message}
                                </div>
                            )}
                            <div className="flex-1 overflow-y-auto">
                                <ul>
                                    {lines.map((line, idx) => {
                                        line = line?.trim();
                                        if (line.startsWith("--->")) {
                                            return null;
                                        }
                                        const makeBold = line.startsWith("CoreApi") || line.startsWith("Omni") || line.startsWith("MegaRadarFirmware");
                                        const extraSpace = line.startsWith("-");
                                        var src = line.split(" in ");
                                        let lineText: string | React.ReactElement = line;
                                        if (src.length === 2) {
                                            lineText = (
                                                <>
                                                    <span className={cn({ "font-semibold": makeBold })}>{src[0]}</span>
                                                    <span className="text-brand-primary block ml-4">{" at " + src[1]}</span>
                                                </>
                                            );
                                        }
                                        return (
                                            <li key={idx} className={cn({ "font-semibold": makeBold && src.length === 1, "my-5": extraSpace })}>
                                                {lineText}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    );
                },
                { className: "w-[75%] h-[75%] flex flex-col overflow-hidden", closeOnOutsideClick: true, showOkButton: false, showCancelButton: false }
            )}
        </>
    );
};
